import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import staticResources from '../data/nil-resources.json';
import { HeartPulse, Trophy, ArrowRightLeft, ArrowRight, Search, Calendar, ExternalLink, CheckCircle, ShieldCheck, BookOpen, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCTS = [
  { to: '/quote', icon: HeartPulse, accent: '#4ECDC4', badge: 'Critical Injury', title: 'Critical Injury Insurance', desc: 'Protect an athlete\u2019s NIL contract value if a covered injury ends or interrupts their season.', testid: 'quote-critical-injury-btn' },
  { to: '/quote-performance-bonus', icon: Trophy, accent: '#F59E0B', badge: 'Performance Bonus', title: 'Performance Bonus & Incentive', desc: 'Insure performance bonus pools against team milestone triggers \u2014 wins, championships, tournament runs.', testid: 'quote-performance-btn' },
  { to: '/quote-transfer', icon: ArrowRightLeft, accent: '#6B5CE7', badge: 'Athlete Transfer', title: 'Athlete Transfer Insurance', desc: 'Protect your NIL investment when an athlete transfers to another program \u2014 built around contract value.', testid: 'quote-transfer-btn' },
];

const TYPE_LABELS = { 'case-study': 'Case Study', 'one-pager': 'One-Pager', 'sales-deck': 'Sales Deck', 'white-paper': 'White Paper', 'article': 'Article', 'availability-report': 'Availability Report' };
const TYPE_GRADIENT = {
  'case-study': 'linear-gradient(90deg, #4ECDC4, #2BA69F)',
  'one-pager': 'linear-gradient(135deg, #6B5CE7, #4ECDC4)',
  'sales-deck': 'linear-gradient(90deg, #6B5CE7, #9B8FFF)',
  'white-paper': 'linear-gradient(90deg, #E040FB, #FF6B35)',
  'article': 'linear-gradient(90deg, #FF6B35, #FFD93D)',
  'availability-report': 'linear-gradient(90deg, #0EA5E9, #4ECDC4)',
};
const TYPE_CHIP = {
  'case-study': { bg: 'rgba(78,205,196,0.12)', color: '#4ECDC4' },
  'one-pager': { bg: 'rgba(107,92,231,0.15)', color: '#7B6FE8' },
  'sales-deck': { bg: 'rgba(107,92,231,0.15)', color: '#9B8FFF' },
  'white-paper': { bg: 'rgba(224,64,251,0.12)', color: '#E879F9' },
  'article': { bg: 'rgba(255,107,53,0.12)', color: '#FF8A5B' },
  'availability-report': { bg: 'rgba(14,165,233,0.14)', color: '#38BDF8' },
};

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

const TeamworksLandingPage = () => {
  const [unlocked, setUnlocked] = useState(() => localStorage.getItem('nil_access') === 'true');
  const [form, setForm] = useState({ full_name: '', organization: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [uploaded, setUploaded] = useState([]);

  useEffect(() => {
    fetch(`${API}/public/resources`)
      .then((r) => r.ok ? r.json() : [])
      .then((data) => setUploaded(data.map((r) => ({ ...r, contentFile: `${API}/public/resources/${r.id}/view` }))))
      .catch(() => {});
  }, []);

  const resources = useMemo(() => [...uploaded, ...staticResources], [uploaded]);

  const filtered = useMemo(() => {
    let list = resources;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter((r) => r.title.toLowerCase().includes(q) || r.description.toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => {
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      return new Date(b.dateAdded) - new Date(a.dateAdded);
    });
  }, [searchTerm, resources]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.organization || !form.email) { setFormError('All fields are required'); return; }
    setSubmitting(true); setFormError('');
    try {
      const res = await fetch(`${API}/resource-leads`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form)
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Submission failed'); }
      localStorage.setItem('nil_access', 'true');
      setUnlocked(true);
    } catch (err) { setFormError(err.message); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* HEADER */}
      <header className="sticky top-0 z-50 flex items-center justify-between px-6 py-4 bg-[#0A0A0A]/80 backdrop-blur-md border-b border-[#4ECDC4]/20">
        <div className="flex items-center gap-4">
          <img src="/nil-resources/logos/logo-white-transparent.png" alt="Players Health" className="h-8 md:h-9" data-testid="header-logo" />
          <span className="h-6 w-px bg-white/15" />
          <img src="/nil-resources/logos/teamworks-white.png" alt="Teamworks" className="h-5 md:h-6" data-testid="header-teamworks-logo" />
        </div>
        <button onClick={() => scrollTo('coverage')} data-testid="nav-request-quote-btn"
          className="px-4 py-2 bg-[#4ECDC4] text-[#0A0A0A] text-sm font-bold uppercase tracking-wide rounded-md hover:-translate-y-0.5 hover:shadow-[0_0_15px_rgba(78,205,196,0.5)] transition-all duration-300">
          Request a Quote
        </button>
      </header>

      {/* HERO */}
      <section className="relative w-full min-h-[78vh] flex flex-col justify-center px-6 overflow-hidden">
        <div className="absolute inset-0 bg-cover" style={{ backgroundImage: "url('/nil-resources/photos/photo-basketball-hero.webp')", backgroundPosition: 'center right' }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/85 via-[#0A0A0A]/75 to-[#0A0A0A]" />
        <div className="relative z-10 max-w-5xl mx-auto w-full py-20">
          <p className="text-[#4ECDC4] text-xs font-mono uppercase tracking-[0.2em] mb-5">Players Health NIL</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-none mb-6 max-w-3xl" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>
            Protect your <span className="text-[#4ECDC4]">investment</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-300 leading-relaxed max-w-2xl mb-10">
            Comprehensive insurance and a full resource library for athletic departments, collectives, and the athletes they back. Request a quote or unlock the library below.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <button onClick={() => scrollTo('coverage')} data-testid="hero-request-quote-btn"
              className="px-8 py-4 bg-[#4ECDC4] text-[#0A0A0A] font-bold uppercase tracking-wide rounded-md hover:-translate-y-1 hover:shadow-[0_0_18px_rgba(78,205,196,0.5)] transition-all duration-300">
              Request a Quote
            </button>
            <button onClick={() => scrollTo('library')} data-testid="hero-resource-library-btn"
              className="px-8 py-4 border border-[#4ECDC4] text-[#4ECDC4] font-bold uppercase tracking-wide rounded-md hover:bg-[#4ECDC4]/10 hover:-translate-y-1 transition-all duration-300">
              Resource Library
            </button>
          </div>
        </div>
      </section>

      {/* COVERAGE / PRODUCTS */}
      <section id="coverage" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <p className="text-[#4ECDC4] text-xs font-mono uppercase tracking-[0.2em] mb-3">Insurance Coverage</p>
          <h2 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-4 max-w-2xl" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>
            Three ways to protect NIL value
          </h2>
          <p className="text-gray-400 text-base leading-relaxed max-w-2xl mb-14">
            Pick the coverage that fits your situation. Every request routes straight to a licensed Players Health advisor.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {PRODUCTS.map((p) => (
              <div key={p.to} className="group bg-[#0F172A] rounded-xl p-7 flex flex-col border transition-all duration-300 hover:-translate-y-1.5"
                style={{ borderColor: `${p.accent}40` }}
                onMouseEnter={(e) => (e.currentTarget.style.boxShadow = `0 14px 40px ${p.accent}22`)}
                onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'none')}>
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-6" style={{ backgroundColor: `${p.accent}1F` }}>
                  <p.icon className="w-6 h-6" style={{ color: p.accent }} />
                </div>
                <p className="text-[11px] font-mono font-semibold uppercase tracking-[0.15em] mb-2" style={{ color: p.accent }}>{p.badge}</p>
                <h3 className="text-white font-bold text-xl mb-3" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>{p.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-7 flex-1">{p.desc}</p>
                <Link to={p.to} data-testid={p.testid}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md font-bold uppercase tracking-wide text-sm transition-all duration-300 hover:-translate-y-0.5"
                  style={{ backgroundColor: p.accent, color: '#0A0A0A' }}>
                  Get Quote <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>

          {/* Trust strip */}
          <div className="flex flex-wrap gap-8 mt-14 pt-10 border-t border-white/10">
            {[
              { icon: ShieldCheck, label: 'Licensed advisors, no automated guesses' },
              { icon: FileText, label: 'Bindable quotes in 1\u20132 business days' },
              { icon: BookOpen, label: 'Full NIL resource library included' },
            ].map((t, i) => (
              <div key={i} className="flex items-center gap-3 text-gray-300 text-sm">
                <t.icon className="w-5 h-5 text-[#4ECDC4]" /> {t.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NIL RESOURCE LIBRARY (Gated) */}
      <section id="library" className="relative py-24 px-6 border-t border-white/10">
        <div className="absolute inset-0 grid grid-cols-2 md:grid-cols-3 opacity-[0.12]">
          <div className="bg-cover bg-center" style={{ backgroundImage: "url('/nil-resources/photos/photo-basketball.png')" }} />
          <div className="bg-cover bg-center" style={{ backgroundImage: "url('/nil-resources/photos/photo-soccer.jpg')" }} />
          <div className="bg-cover bg-center hidden md:block" style={{ backgroundImage: "url('/nil-resources/photos/photo-tennis-topview.png')" }} />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A]/90 to-[#0A0A0A]" />

        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[#6B5CE7] text-xs font-mono uppercase tracking-[0.2em] mb-3">NIL Resource Library</p>
            <h2 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-3" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>
              Case studies, guides & briefings
            </h2>
            <p className="text-gray-400 text-base max-w-xl mx-auto">Built with the Players Health brand standard — everything your program needs to understand and insure NIL risk.</p>
          </div>

          {!unlocked ? (
            <div className="max-w-md mx-auto bg-white rounded-xl p-8 shadow-2xl" data-testid="lead-form-container">
              <h3 className="text-[#1A1A1A] text-2xl font-bold mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>Access the Library</h3>
              <p className="text-[#64748B] text-sm mb-6">Enter your details to unlock every resource.</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#475569] mb-1">Full Name</label>
                  <input type="text" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    className="w-full px-4 py-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg text-sm text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#6B5CE7] focus:border-transparent transition"
                    placeholder="Your full name" data-testid="lead-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#475569] mb-1">School / Organization</label>
                  <input type="text" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })}
                    className="w-full px-4 py-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg text-sm text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#6B5CE7] focus:border-transparent transition"
                    placeholder="University or organization" data-testid="lead-org-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#475569] mb-1">Email</label>
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-4 py-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg text-sm text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#6B5CE7] focus:border-transparent transition"
                    placeholder="you@school.edu" data-testid="lead-email-input" />
                </div>
                {formError && <p className="text-red-500 text-sm" data-testid="lead-form-error">{formError}</p>}
                <button type="submit" disabled={submitting} data-testid="unlock-library-btn"
                  className="w-full py-3 rounded-lg font-bold text-white text-sm uppercase tracking-wide bg-[#6B5CE7] hover:bg-[#7B6FE8] transition-all duration-300 disabled:opacity-50">
                  {submitting ? 'Submitting...' : 'Unlock Library'}
                </button>
              </form>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-center gap-2 mb-8" data-testid="access-granted">
                <CheckCircle className="w-5 h-5 text-[#4ECDC4]" />
                <span className="text-[#4ECDC4] text-sm font-semibold uppercase tracking-wide">Access granted</span>
              </div>
              <div className="max-w-xl mx-auto mb-10">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                  <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search resources..."
                    className="w-full pl-11 pr-4 py-3 bg-[#1A1A1A] border border-[#333] rounded-lg text-sm text-white placeholder-[#666] focus:outline-none focus:ring-2 focus:ring-[#6B5CE7] focus:border-transparent"
                    data-testid="library-search-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map((r) => {
                  const chip = TYPE_CHIP[r.type] || TYPE_CHIP['article'];
                  const dateFmt = new Date(r.dateAdded).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                  return (
                    <div key={r.id} onClick={() => window.open(r.contentFile, '_blank', 'noopener,noreferrer')} role="link" tabIndex={0} data-testid="resource-card-link"
                      className="group cursor-pointer no-underline bg-[#0F172A] border border-white/10 rounded-xl overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-1 hover:border-[#4ECDC4]/40 hover:shadow-[0_10px_30px_rgba(78,205,196,0.12)]">
                      <div className="h-1 w-full" style={{ background: TYPE_GRADIENT[r.type] || TYPE_GRADIENT['article'] }} />
                      <div className="p-5 flex-1 flex flex-col">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide self-start mb-3" style={{ background: chip.bg, color: chip.color }}>
                          {TYPE_LABELS[r.type] || r.type}
                        </span>
                        <h3 className="text-white font-bold text-base leading-tight mb-2 group-hover:text-[#4ECDC4] transition-colors" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>{r.title}</h3>
                        <p className="text-gray-400 text-sm leading-relaxed flex-1 line-clamp-3">{r.description}</p>
                      </div>
                      <div className="px-5 py-3 border-t border-white/10 flex items-center justify-between">
                        <span className="flex items-center gap-1.5 text-xs text-[#666]"><Calendar className="w-3 h-3" />{dateFmt}</span>
                        <span className="flex items-center gap-1 text-sm font-semibold text-[#4ECDC4] group-hover:underline">View <ExternalLink className="w-3 h-3" /></span>
                      </div>
                    </div>
                  );
                })}
              </div>
              {filtered.length === 0 && (
                <div className="text-center py-16">
                  <FileText className="w-12 h-12 text-[#444] mx-auto mb-4" />
                  <p className="text-[#666]">No resources found</p>
                </div>
              )}
              <p className="text-sm text-[#666] text-center mt-8">Showing {filtered.length} of {resources.length} resources</p>
            </>
          )}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#0A0A0A] py-14 px-6 border-t border-white/10">
        <div className="max-w-6xl mx-auto flex flex-col items-center text-center">
          <div className="flex items-center gap-5 mb-6">
            <img src="/nil-resources/logos/logo-white-transparent.png" alt="Players Health" className="h-10" data-testid="footer-logo" />
            <span className="h-7 w-px bg-white/15" />
            <img src="/nil-resources/logos/teamworks-white.png" alt="Teamworks" className="h-6" data-testid="footer-teamworks-logo" />
          </div>
          <div className="flex items-center gap-6 mb-6">
            <Link to="/insurance" className="text-gray-400 text-sm hover:text-[#4ECDC4] transition-colors">Insurance</Link>
            <button onClick={() => scrollTo('library')} className="text-gray-400 text-sm hover:text-[#4ECDC4] transition-colors">Resources</button>
            <button onClick={() => scrollTo('coverage')} className="text-gray-400 text-sm hover:text-[#4ECDC4] transition-colors">Request a Quote</button>
          </div>
          <p className="text-[#555] text-xs">&copy; {new Date().getFullYear()} Players Health. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default TeamworksLandingPage;
